/**
 * Algoritmo de agrupamiento de álbumes de imágenes (MOMSG-1172, estilo
 * WhatsApp), compartido entre dashboard y widget — NO forkear entre las dos
 * superficies aunque el organism de conversación sí esté duplicado (ver
 * plan MOMSG-1172, sección 3.1).
 *
 * Agrupa SOLO corridas CONSECUTIVAS de mensajes con el mismo `albumId`. El
 * agrupamiento vive en el frontend, no en el backend, porque: el backend
 * pagina por `sequence` con cursor `nextBeforeId` (un álbum a caballo de un
 * borde de página rompería `hasMore`); `messages:around` centra la ventana
 * en un `messageId` individual; `findMediaPage` (pestaña Archivos) necesita
 * filas individuales.
 */

/** Cantidad mínima de fotos consecutivas con el mismo albumId para renderizar como grid. Una corrida de 1 (borde de paginación, o legacy) cae al render de burbuja simple existente. */
export const CHAT_ALBUM_MIN_ITEMS = 2;

/** Máximo de tiles visibles en el grid; el resto se resume con un overlay "+N" sobre el último tile. */
export const CHAT_ALBUM_MAX_TILES = 4;

/**
 * Forma mínima que necesita `groupConsecutiveAlbums` de cada mensaje —
 * duck-typing deliberado: dashboard y widget tienen cada uno su propio tipo
 * `ChatMessage` local (no comparten uno de `lib-klab-core`), así que esta
 * interfaz evita atar el algoritmo compartido a cualquiera de los dos.
 */
export interface AlbumGroupable {
  readonly id: string;
  readonly albumId?: string | null;
  readonly albumIndex?: number | null;
  readonly albumCount?: number | null;
  readonly isDeleted?: boolean;
}

/**
 * Corrida de N mensajes consecutivos con el mismo `albumId`, lista para
 * renderizarse como grid. `albumCount` es el máximo declarado por los
 * miembros presentes (nunca el `messages.length` a secas): si el álbum
 * está parcialmente cargado (borde de paginación) o algún miembro falló al
 * enviarse, `albumCount` sigue reflejando el total real declarado por el
 * cliente al momento del envío — solo alimenta el badge "+N", nunca
 * bloquea ni retrasa el render de lo que sí está disponible.
 */
export interface AlbumRun<T extends AlbumGroupable> {
  readonly type: 'album';
  readonly id: string;
  readonly albumId: string;
  readonly messages: readonly T[];
  readonly albumCount: number;
}

/**
 * Agrupa mensajes consecutivos en corridas de álbum.
 *
 * Casos borde (ver tabla del plan MOMSG-1172, sección 3.1):
 * - Álbum parcial en el borde: agrupa lo que hay, `albumCount` no bloquea.
 * - Texto intercalado en medio del álbum: parte la corrida en dos grupos.
 * - Miembro en vuelo/fallido (mismo albumId, no borrado): sigue en la corrida.
 * - Miembro borrado (`isDeleted`): se excluye, se renderiza aparte como
 *   mensaje suelto ("Mensaje eliminado"), parte la corrida.
 * - Mensajes viejos sin `albumId`: nunca agrupan → álbum de 1 → idéntico al
 *   render actual (por eso una corrida de largo 1 vuelve a mensaje suelto,
 *   ver CHAT_ALBUM_MIN_ITEMS).
 *
 * @param enabled Si es `false` (ej. modo de selección de reenvío, FASE 5),
 * devuelve `messages` sin ningún agrupamiento.
 */
export function groupConsecutiveAlbums<T extends AlbumGroupable>(
  messages: readonly T[],
  enabled: boolean,
): readonly (T | AlbumRun<T>)[] {
  if (!enabled) return messages;

  const result: (T | AlbumRun<T>)[] = [];
  let currentRun: T[] = [];

  const flushCurrentRun = (): void => {
    if (currentRun.length === 0) return;
    if (currentRun.length >= CHAT_ALBUM_MIN_ITEMS) {
      const albumId = currentRun[0].albumId as string;
      const albumCount = currentRun.reduce(
        (max, message) => Math.max(max, message.albumCount ?? 0),
        currentRun.length,
      );
      // Orden por albumIndex (orden de SELECCIÓN del usuario), no por orden
      // de llegada/sequence: un reintento de subida fallida puede persistir
      // más tarde que una foto de índice posterior que sí salió a la
      // primera. Sin este sort, el orden de los tiles del grid podía
      // desalinearse del de la tira del lightbox (buildAlbumLightboxItems),
      // que sí ordena por albumIndex — hallazgo de revisión adversarial.
      const orderedMessages = [...currentRun].sort(
        (a, b) => (a.albumIndex ?? 0) - (b.albumIndex ?? 0),
      );
      result.push({
        type: 'album',
        // `id` incluye el id del primer mensaje (en orden de llegada, antes
        // de reordenar) para que dos corridas del MISMO albumId partidas por
        // un mensaje intercalado (ver caso de abajo) nunca compartan `id` —
        // sin esto, un *ngFor con trackBy por `id` trata a ambas corridas
        // como la misma entrada y puede mezclar su estado/DOM entre sí
        // (hallazgo CRÍTICO de revisión adversarial).
        id: `album-${albumId}-${currentRun[0].id}`,
        albumId,
        messages: orderedMessages,
        albumCount,
      });
    } else {
      result.push(...currentRun);
    }
    currentRun = [];
  };

  for (const message of messages) {
    // Un mensaje borrado nunca sigue "reclamando" pertenencia a la corrida
    // (aunque su `albumId` original siga poblado) — se excluye del grid y
    // se renderiza como burbuja "Mensaje eliminado" normal, partiendo
    // cualquier corrida en curso.
    if (message.isDeleted) {
      flushCurrentRun();
      result.push(message);
      continue;
    }

    const currentAlbumId = currentRun[0]?.albumId ?? null;
    if (message.albumId && message.albumId === currentAlbumId) {
      currentRun.push(message);
      continue;
    }

    flushCurrentRun();

    if (message.albumId) {
      currentRun = [message];
    } else {
      result.push(message);
    }
  }
  flushCurrentRun();

  return result;
}
